import { useState } from "react";
import { Link } from "react-router-dom";
import { Truck, AlertTriangle, ClipboardX, ListChecks, Siren } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { PageHeader, Kpi, Panel, Loading, Empty, Plate, LevelBadge, Chip, ExportBtn, FilterBar, Field, NSelect, useFetch, AN_STATUS } from "@/components/common";
import { Input } from "@/components/ui/input";
import { fDate, fNum } from "@/lib/api";
import { AdBluePanel } from "@/components/AdBlue";

export function useLivOptions() {
  const v = useFetch("/vehicles", { type: "livraison" });
  const d = useFetch("/drivers", { status: "tous" });
  return {
    vehicles: (v.data || []).map((x) => ({ value: x.id, label: `${x.plate} · ${x.brand} ${x.model}` })),
    drivers: (d.data || []).map((x) => ({ value: x.id, label: x.name })),
  };
}

export function LivFilters({ f, setF, withStatus = true, testid = "liv-filters" }) {
  const o = useLivOptions();
  const set = (k) => (v) => setF({ ...f, [k]: v });
  return (
    <FilterBar testid={testid}>
      <Field label="Véhicule"><NSelect value={f.vehicle_id} onChange={set("vehicle_id")} options={o.vehicles} placeholder="Tous" testid="filter-vehicle" /></Field>
      <Field label="Chauffeur"><NSelect value={f.driver_id} onChange={set("driver_id")} options={o.drivers} placeholder="Tous" testid="filter-driver" /></Field>
      <Field label="Du"><Input type="date" value={f.date_from} onChange={(e) => set("date_from")(e.target.value)} className="h-10 bg-white" data-testid="filter-date-from" /></Field>
      <Field label="Au"><Input type="date" value={f.date_to} onChange={(e) => set("date_to")(e.target.value)} className="h-10 bg-white" data-testid="filter-date-to" /></Field>
      {withStatus && (
        <Field label="Statut anomalie">
          <NSelect value={f.status} onChange={set("status")} placeholder="Tous" testid="filter-status"
            options={[{ value: "ouvertes", label: "Non résolues" }, ...Object.entries(AN_STATUS).map(([k, v]) => ({ value: k, label: v[0] }))]} />
        </Field>
      )}
      <div className="flex items-end">
        <button onClick={() => setF({})} className="h-10 px-3 text-sm font-semibold text-slate-500 hover:text-[#0E2D43]" data-testid="filter-reset">Réinitialiser</button>
      </div>
    </FilterBar>
  );
}

const sev = (s) => (s === "danger" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200");

export default function LivDashboard() {
  const [f, setF] = useState({});
  const { data, loading } = useFetch("/livraison/dashboard", f);
  return (
    <div data-testid="livraison-dashboard">
      <PageHeader overline="Véhicules de livraison" title="Tableau de bord">
        <ExportBtn path="/export/checks" params={f} filename="controles.xlsx" label="Excel contrôles" testid="export-checks-xlsx" />
        <ExportBtn path="/export/pdf/livraison" params={f} filename="synthese-livraison.pdf" label="Synthèse PDF" pdf testid="export-livraison-pdf" />
      </PageHeader>
      <LivFilters f={f} setF={setF} />
      {loading || !data ? <Loading /> : <Body d={data} />}
    </div>
  );
}

function Body({ d }) {
  const k = d.kpis;
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Kpi testid="kpi-active-vehicles" label="Véhicules actifs" value={k.active_vehicles} sub={`${k.total_vehicles} en parc`} icon={Truck} tone="navy" />
        <Kpi testid="kpi-vehicles-anomaly" label="Véhicules avec anomalie" value={k.vehicles_with_anomaly} icon={AlertTriangle} tone="danger" delay={1} />
        <Kpi testid="kpi-missing-checks" label="Contrôles manquants" value={k.missing_checks} sub={`${k.maintenance_due} entretien(s) à prévoir`} icon={ClipboardX} delay={2} />
        <Kpi testid="kpi-open-anomalies" label="Anomalies non résolues" value={k.open_anomalies} sub={`${k.checks_count} contrôles sur la période`} icon={ListChecks} tone="danger" delay={3} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6" data-testid="accident-kpis">
        <Kpi testid="kpi-accidents-open" label="Accidents ouverts" value={k.accidents_open} icon={Siren} tone="danger" />
        <Kpi testid="kpi-accidents-immobilized" label="Immobilisés suite accident" value={k.accidents_immobilized} icon={Truck} tone="danger" delay={1} />
        <Kpi testid="kpi-accidents-to-complete" label="Dossiers à compléter" value={k.accidents_to_complete} icon={ClipboardX} delay={2} />
        <Kpi testid="kpi-accidents-pending-repair" label="Réparations en attente" value={k.accidents_pending_repair} icon={ListChecks} delay={3} />
      </div>
      <AdBluePanel />
      <div className="grid xl:grid-cols-3 gap-6 mb-6">
        <Panel title="Kilométrage par véhicule" className="xl:col-span-2" testid="chart-mileage">
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={d.mileage}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="plate" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v) => `${fNum(v)} km`} />
                <Bar dataKey="mileage" radius={[4, 4, 0, 0]}>
                  {d.mileage.map((m) => <Cell key={m.plate} fill={m.status === "actif" ? "#0E2D43" : "#F49619"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Alertes importantes" testid="liv-alerts" flush>
          {d.alerts.length === 0 ? <Empty text="Aucune alerte" /> : (
            <ul className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
              {d.alerts.map((a) => (
                <li key={a.id}>
                  <Link to={`/livraison/vehicules/${a.vehicle_id}`} className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-[#FFF8EE] transition-colors" data-testid={`alert-${a.id}`}>
                    <span className="flex items-center gap-2 min-w-0"><Chip cls={sev(a.severity)}>{a.plate}</Chip><span className="text-sm truncate">{a.label}</span></span>
                    <span className="text-xs text-slate-400 shrink-0">{fDate(a.date)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
      <div className="grid xl:grid-cols-3 gap-6">
        <Panel title="Derniers contrôles réalisés" className="xl:col-span-2" flush testid="latest-checks">
          <ChecksTable checks={d.latest_checks} />
        </Panel>
        <Panel title="Anomalies par statut" testid="anomalies-by-status">
          <div className="space-y-3">
            {d.anomalies_by_status.map((s) => (
              <div key={s.status} className="flex items-center justify-between">
                <Chip cls={AN_STATUS[s.status][1]}>{s.label}</Chip>
                <span className="font-display font-bold text-lg text-[#0E2D43]" data-testid={`status-count-${s.status}`}>{s.count}</span>
              </div>
            ))}
          </div>
          <Link to="/livraison/anomalies" className="mt-5 inline-block text-sm font-semibold text-[#E0830B]" data-testid="link-anomalies">Gérer les anomalies →</Link>
        </Panel>
      </div>
    </>
  );
}

export function ChecksTable({ checks, showPlate = true }) {
  if (!checks.length) return <Empty text="Aucun contrôle" />;
  return (
    <div className="overflow-x-auto">
      <table className="tbl w-full" data-testid="checks-table">
        <thead>
          <tr>
            <th>Date</th>{showPlate && <th>Véhicule</th>}<th>Chauffeur attribué</th><th>Contrôlé par</th><th className="text-right">Km</th>
            <th>Voyants</th><th>Freins</th><th>Pneus</th><th>Dommages</th><th>Commentaire</th>
          </tr>
        </thead>
        <tbody>
          {checks.map((c) => (
            <tr key={c.id} data-testid={`check-row-${c.id}`}>
              <td>{fDate(c.date)}</td>
              {showPlate && <td><Link to={`/livraison/vehicules/${c.vehicle_id}`}><Plate>{c.plate}</Plate></Link></td>}
              <td className="font-semibold">{c.driver_name}</td><td className="text-slate-500">{c.controller_name}</td>
              <td className="text-right tabular-nums">{fNum(c.mileage)}</td>
              <td><LevelBadge v={c.voyants} /></td><td><LevelBadge v={c.freins} /></td>
              <td><LevelBadge v={c.pneus} /></td><td><LevelBadge v={c.dommages} /></td>
              <td className="max-w-[220px] truncate text-slate-500">{c.comment || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
